import React, { useState, useMemo } from 'react';
import { USER_TIERS } from '../../../constants/tiers';
import CosmicIcon from '../../common/CosmicIcon';
import './CosmicDomainsGalaxy.css';

// Structure's brand purple (#54548E) is too dark to read on the navy surface,
// so its strength bars and modal accents vanish next to the other domains.
// Lighten Structure specifically for visual fills; do NOT touch the global
// token / DOMAIN_COLORS (used elsewhere, including scoring-adjacent surfaces).
const getDisplayColor = (domainId, domainColors) =>
  domainId === 'structure' ? '#8B8BC4' : domainColors[domainId];

/**
 * Cosmic Domains Galaxy - Mobile-optimized domain exploration
 * Single-column layout with expandable domain sections
 * Displays element descriptors (e.g., "Analyst") instead of full names
 * 
 * Tier-based access:
 * - Free: All 5 domain scores visible, top 3 scoring elements within top 2 domains
 * - Enhanced: All domain scores visible, top 8 scoring elements across all domains
 * - Premium: All domain scores and all elements visible
 */
const CosmicDomainsGalaxy = ({ results, tier, domainColors, domains, preferences, hideHeader = false }) => {
  const [expandedDomains, setExpandedDomains] = useState(new Set());
  const [selectedElement, setSelectedElement] = useState(null);
  
  const toggleDomain = (domainId) => {
    const newExpanded = new Set(expandedDomains);
    if (newExpanded.has(domainId)) {
      newExpanded.delete(domainId);
    } else {
      newExpanded.add(domainId);
    }
    setExpandedDomains(newExpanded);
  };
  
  const handleElementClick = (element, isUnlocked) => {
    setSelectedElement({ ...element, isUnlocked });
  };
  
  // Sort domains by user's scores and add rank
  const sortedDomains = domains
    .map(domain => ({
      ...domain,
      userScore: results?.domains?.[domain.id] || 0
    }))
    .sort((a, b) => b.userScore - a.userScore)
    .map((domain, index) => ({
      ...domain,
      domainRank: index + 1
    }));
  
  // Get top 2 domain IDs for Free tier
  const topTwoDomainIds = useMemo(() => {
    return sortedDomains.slice(0, 2).map(d => d.id);
  }, [sortedDomains]);
  
  // Pre-compute unlocked element IDs using score-based logic (matches Domains tab)
  const unlockedElementIds = useMemo(() => {
    if (tier === USER_TIERS.PREMIUM) return null; // Premium sees all
    if (!results?.topPreferences) return new Set();
    
    const allElements = [...results.topPreferences]
      .map(el => ({
        ...el,
        normalizedScore: el.strengthPercentile || (el.score > 1 ? el.score : el.score * 100)
      }))
      .sort((a, b) => b.normalizedScore - a.normalizedScore);
    
    if (tier === USER_TIERS.ENHANCED) {
      // Enhanced: Top 8 scoring elements across ALL domains (full constellation)
      return new Set(allElements.slice(0, 8).map(el => el.id || el.elementId));
    }
    
    // Free tier: Top 3 scoring elements within top 2 domains only
    const elementsInTop2 = allElements.filter(el => topTwoDomainIds.includes(el.domainId));
    return new Set(elementsInTop2.slice(0, 3).map(el => el.id || el.elementId));
  }, [tier, results?.topPreferences, topTwoDomainIds]);
  
  // Check if domain score is visible
  // All tiers see all 5 domain scores — element-level detail is tier-gated separately
  const isDomainScoreVisible = (domainRank) => {
    return true; // All tiers see all 5 domain percentages
  };
  
  return (
    <div className="cosmic-domains-galaxy">
      
      {/* Galaxy Header - Can be hidden when rendered separately */}
      {!hideHeader && (
        <div className="cosmic-card cosmic-card--professional">
          <div className="cosmic-card__header">
            <h3 className="cosmic-card__title"><CosmicIcon name="results_domains" size={22} decorative /> Your Career Galaxy</h3>
            <p className="cosmic-card__subtitle">
              Explore all 45 elements across five cosmic domains
            </p>
          </div>
        </div>
      )}
      
      {/* Vertical Domain Stacks */}
      <div className="cosmic-domains-stack">
        {sortedDomains.map(domain => (
          <CosmicDomainStack
            key={domain.id}
            domain={domain}
            userResults={results}
            tier={tier}
            domainColors={domainColors}
            preferences={preferences}
            isExpanded={expandedDomains.has(domain.id)}
            onToggle={() => toggleDomain(domain.id)}
            onElementClick={handleElementClick}
            USER_TIERS={USER_TIERS}
            isDomainScoreVisible={isDomainScoreVisible(domain.domainRank)}
            unlockedElementIds={unlockedElementIds}
          />
        ))}
      </div>
      
      {/* Element Detail Modal */}
      {selectedElement && (
        <CosmicElementModal
          element={selectedElement}
          isUnlocked={selectedElement.isUnlocked}
          tier={tier}
          domainColors={domainColors}
          onClose={() => setSelectedElement(null)}
          USER_TIERS={USER_TIERS}
        />
      )}
      
    </div>
  );
};

/**
 * Access Indicator Component
 */
const CosmicAccessIndicator = ({ tier, totalElements, accessibleElements }) => {
  const percentage = Math.round((accessibleElements / totalElements) * 100);
  
  // Get tier class for styling (matches tier badge and progress bar colors)
  const tierClass = tier === USER_TIERS.PREMIUM ? 'premium' 
    : tier === USER_TIERS.ENHANCED ? 'enhanced' 
    : 'free';

  // Canonical tier names + glyphs (de-pilled, flat marker — not a giant pill).
  const tierDisplayName = tierClass === 'premium' ? 'Complete Universe'
    : tierClass === 'enhanced' ? 'Stellar Map'
    : 'First Light';
  const tierIconName = `tier_${tierClass}`;

  return (
    <div className={`cosmic-access-indicator cosmic-access-indicator--${tierClass}`}>
      <div className="cosmic-access-info">
        <div className="cosmic-access-stats">
          <span className="cosmic-access-number">{accessibleElements}</span>
          <span className="cosmic-access-total">/ {totalElements}</span>
          <span className="cosmic-access-label">elements unlocked</span>
        </div>
        <div className={`cosmic-tier-marker cosmic-tier-marker--${tierClass}`}>
          <span className="cosmic-tier-marker__icon">
            <CosmicIcon name={tierIconName} size={18} mono decorative />
          </span>
          <span className="cosmic-tier-marker__name">{tierDisplayName}</span>
        </div>
      </div>
      
      <div className="cosmic-access-bar">
        <div className="cosmic-access-track">
          <div 
            className="cosmic-access-fill"
            style={{ width: `${percentage}%` }}
          />
        </div>
        <span className="cosmic-access-percentage">{percentage}%</span>
      </div>
    </div>
  );
};

/**
 * Domain Stack Component
 */
const CosmicDomainStack = ({ 
  domain, userResults, tier, domainColors, preferences,
  isExpanded, onToggle, onElementClick, USER_TIERS,
  isDomainScoreVisible, unlockedElementIds
}) => {
  const unlockedElements = getUnlockedDomainElementsNew(domain.id, userResults, tier, preferences, USER_TIERS, unlockedElementIds);
  const lockedElements = getLockedDomainElementsNew(domain.id, userResults, tier, preferences, USER_TIERS, unlockedElementIds);
  const domainScore = Math.round(userResults?.domains?.[domain.id] || 0);
  
  return (
    <div 
      className={`cosmic-domain-stack ${!isDomainScoreVisible ? 'cosmic-domain-stack--locked' : ''}`}
      style={{'--domain-color': getDisplayColor(domain.id, domainColors)}}
    >
      
      {/* Domain Header - Clickable */}
      <div 
        className={`cosmic-domain-header ${isExpanded ? 'cosmic-domain-header--open' : ''}`}
        onClick={onToggle}
        role="button"
        tabIndex={0}
        aria-expanded={isExpanded}
        onKeyPress={(e) => e.key === 'Enter' && onToggle()}
      >
        <div className="cosmic-domain-info">
          {/* Rank leads the row, far left */}
          {isDomainScoreVisible && (
            <span className="cosmic-domain-rank-num">#{domain.domainRank}</span>
          )}
          <div className="cosmic-domain-icon"><CosmicIcon name={`domain_${domain.id}`} size={36} decorative /></div>
          <h4 className="cosmic-domain-name">{domain.name}</h4>
        </div>
        
        {/* Domain Score Bar - visual strength (relative length). The numeric
            percentage is hidden by product decision; the rank now sits beside
            the title above. */}
        {isDomainScoreVisible ? (
          <div className="cosmic-domain-score-bar">
            <div 
              className="cosmic-domain-score-fill"
              style={{ width: `${domainScore}%` }}
            />
          </div>
        ) : (
          <div className="cosmic-domain-locked">
            <span className="cosmic-domain-lock-icon"><CosmicIcon name="ui_locked" size={18} decorative /></span>
          </div>
        )}
        
        {/* Clear, labeled expand affordance (a bare triangle wasn't obviously tappable) */}
        <div className="cosmic-domain-expand-toggle">
          <span className="cosmic-domain-expand-label">
            {isExpanded ? 'Hide elements' : 'View elements'}
          </span>
        </div>
      </div>
      
      {/* Expanded Elements Container */}
      {isExpanded && (
        <div className="cosmic-elements-container">
          
          {/* Unlocked Elements (by score) */}
          {unlockedElements.length > 0 && (
            <div className="cosmic-unlocked-section">
              {unlockedElements.map((element, index) => (
                <CosmicElementCard
                  key={element.id}
                  element={element}
                  domainColor={getDisplayColor(domain.id, domainColors)}
                  isUnlocked={true}
                  onClick={() => onElementClick(element, true)}
                  animationDelay={index * 0.1}
                />
              ))}
            </div>
          )}
          
          {/* Locked Elements (alphabetical) */}
          {lockedElements.length > 0 && (
            <div className="cosmic-locked-section">
              <div className="cosmic-locked-divider">
                <span className="locked-label"><CosmicIcon name="ui_locked" size={14} decorative /> Upgrade to unlock</span>
              </div>
              
              {lockedElements.map((element, index) => (
                <CosmicElementCard
                  key={element.id}
                  element={element}
                  domainColor={getDisplayColor(domain.id, domainColors)}
                  isUnlocked={false}
                  onClick={() => onElementClick(element, false)}
                  animationDelay={(unlockedElements.length * 0.1) + (index * 0.05)}
                />
              ))}
            </div>
          )}
          
        </div>
      )}
      
    </div>
  );
};

/**
 * Element Card Component - Clean, simplified design
 */
const CosmicElementCard = ({ 
  element, domainColor, isUnlocked, 
  onClick, animationDelay 
}) => {
  return (
    <div
      className={`cosmic-element-card ${isUnlocked ? 'unlocked' : 'locked'}`}
      onClick={onClick}
      style={{ 
        '--domain-color': domainColor,
        '--animation-delay': `${animationDelay}s`
      }}
    >
      {/* Card Header with Rank and Name */}
      <div className="cosmic-element-header">
        <div className="cosmic-element-indicator">
          {isUnlocked ? (
            <div className="cosmic-rank-badge">#{element.rank || '?'}</div>
          ) : (
            <div className="cosmic-lock-indicator"><CosmicIcon name="ui_locked" size={16} decorative /></div>
          )}
        </div>
        
        <div className="cosmic-element-title">
          <div className="cosmic-element-name">
            {isUnlocked ? element.personDescriptor : 'Locked'}
          </div>
        </div>
      </div>
      
      {/* Score Display - visual strength bar only; the numeric percentage is
          hidden by product decision (rank shown in the header badge). */}
      <div className="cosmic-element-score-area">
        {isUnlocked && element.score ? (
          <>
            <div className="cosmic-score-bar">
              <div 
                className="cosmic-score-fill"
                style={{ 
                  width: `${element.score}%`,
                  background: `linear-gradient(90deg, ${domainColor}, color-mix(in srgb, ${domainColor} 70%, white))`
                }}
              />
            </div>
          </>
        ) : !isUnlocked ? (
          <span className="cosmic-upgrade-text">Upgrade to unlock</span>
        ) : null}
      </div>
    </div>
  );
};

/**
 * Element Detail Modal - Clean, refined design
 */
const CosmicElementModal = ({ element, isUnlocked, tier, domainColors, onClose, USER_TIERS }) => {
  const domainColor = getDisplayColor(element.domainId, domainColors) || '#4B79A1';
  
  return (
    <div className="cosmic-modal-overlay" onClick={onClose}>
      <div 
        className="cosmic-element-modal" 
        onClick={e => e.stopPropagation()}
        style={{ '--domain-color': domainColor }}
      >
        {/* Close Button */}
        <button className="cosmic-modal-close" onClick={onClose}>✕</button>
        
        {/* Modal Header with Domain Badge */}
        <div className="cosmic-modal-header">
          <div className="cosmic-element-domain-badge">
            {getDomainName(element.domainId)}
          </div>
        </div>
        
        {/* Element Name - Large and Prominent */}
        <h2 className="cosmic-modal-element-name">
          {isUnlocked ? element.personDescriptor : 'Locked Element'}
        </h2>
        
        {/* Score Circle - Visual highlight for unlocked elements */}
        {isUnlocked && element.score ? (
          <div className="cosmic-modal-score-section">
            <div className="cosmic-modal-score-ring" style={{ '--score': element.score }}>
              <svg viewBox="0 0 120 120">
                <circle className="cosmic-score-ring-bg" cx="60" cy="60" r="54" />
                <circle 
                  className="cosmic-score-ring-fill" 
                  cx="60" cy="60" r="54" 
                  style={{ 
                    strokeDasharray: `${(element.score / 100) * 339} 339`,
                    stroke: domainColor
                  }}
                />
              </svg>
              <div className="cosmic-score-ring-value">
                <span className="cosmic-score-number" style={{ fontSize: '28px' }}>#{element.rank || '?'}</span>
              </div>
            </div>
            <div className="cosmic-modal-score-label">Your #{element.rank || '?'} Strength</div>
          </div>
        ) : null}
        
        {/* Upgrade CTA for locked elements */}
        {!isUnlocked && (
          <div className="cosmic-modal-locked-section">
            <div className="cosmic-locked-icon"><CosmicIcon name="ui_locked" size={32} decorative /></div>
            <p className="cosmic-locked-message">
              Upgrade to <strong>{getNextTier(tier, USER_TIERS)}</strong> to unlock this element and see your score.
            </p>
            <button className="cosmic-cta cosmic-cta--sm">
              <CosmicIcon name="ui_bolt" size={16} decorative /> Upgrade Now
            </button>
          </div>
        )}
        
        {/* Element Description (if available) */}
        {isUnlocked && (element.fullDescription || element.description) && (
          <div className="cosmic-modal-description">
            <p>{element.fullDescription || element.description}</p>
          </div>
        )}
      </div>
    </div>
  );
};

// Helper Functions

// New score-based helper functions that use pre-computed unlockedElementIds
const getUnlockedDomainElementsNew = (domainId, userResults, tier, preferences, USER_TIERS, unlockedElementIds) => {
  // Handle preferences as either an object (with domain keys) or an array
  let preferencesArray = [];
  if (Array.isArray(preferences)) {
    preferencesArray = preferences;
  } else if (preferences && typeof preferences === 'object') {
    preferencesArray = Object.entries(preferences)
      .filter(([key]) => typeof preferences[key] !== 'function')
      .flatMap(([domainKey, prefs]) => 
        Array.isArray(prefs) ? prefs.map(p => ({ ...p, domainId: domainKey })) : []
      );
  }
  
  const domainPreferences = preferencesArray
    .filter(pref => pref.domainId === domainId)
    .map(pref => {
      const allElementMatch = userResults?.allElements?.find(
        el => el.elementId === pref.id || el.id === pref.id
      );
      const topPrefMatch = userResults?.topPreferences?.find(up => up.id === pref.id);
      
      const rawScore = allElementMatch?.strengthPercentile || 
                       topPrefMatch?.strengthPercentile || 
                       (topPrefMatch?.score > 1 ? topPrefMatch.score : (topPrefMatch?.score || 0) * 100);
      
      const globalRank = allElementMatch?.rank || 
                         userResults?.topPreferences?.findIndex(up => up.id === pref.id) + 1 || 
                         null;
      
      return {
        ...pref,
        personDescriptor: pref.descriptor || pref.personDescriptor || pref.name,
        score: rawScore,
        rank: globalRank
      };
    });
  
  // For Premium, all elements with scores are unlocked
  if (tier === USER_TIERS.PREMIUM) {
    return [...domainPreferences].sort((a, b) => b.score - a.score);
  }
  
  // For Free/Enhanced, filter by the pre-computed unlockedElementIds set
  return domainPreferences
    .filter(pref => unlockedElementIds?.has(pref.id))
    .sort((a, b) => b.score - a.score);
};

const getLockedDomainElementsNew = (domainId, userResults, tier, preferences, USER_TIERS, unlockedElementIds) => {
  // Premium sees no locked elements
  if (tier === USER_TIERS.PREMIUM) return [];
  
  let preferencesArray = [];
  if (Array.isArray(preferences)) {
    preferencesArray = preferences;
  } else if (preferences && typeof preferences === 'object') {
    preferencesArray = Object.entries(preferences)
      .filter(([key]) => typeof preferences[key] !== 'function')
      .flatMap(([domainKey, prefs]) => 
        Array.isArray(prefs) ? prefs.map(p => ({ ...p, domainId: domainKey })) : []
      );
  }
  
  return preferencesArray
    .filter(pref => pref.domainId === domainId && !unlockedElementIds?.has(pref.id))
    .map(pref => ({
      ...pref,
      personDescriptor: pref.descriptor || pref.personDescriptor || pref.name
    }))
    .sort((a, b) => a.name.localeCompare(b.name));
};

const getTopDomains = (results, count = 2) => {
  if (!results?.domains) return [];
  
  return Object.entries(results.domains)
    .sort(([,a], [,b]) => b - a)
    .slice(0, count)
    .map(([domainId]) => domainId);
};

const getUnlockedDomainElements = (domainId, userResults, tier, preferences, USER_TIERS) => {
  const topDomains = getTopDomains(userResults, 2);
  
  // Handle preferences as either an object (with domain keys) or an array
  let preferencesArray = [];
  if (Array.isArray(preferences)) {
    preferencesArray = preferences;
  } else if (preferences && typeof preferences === 'object') {
    // Convert object format to array, adding domainId to each preference
    preferencesArray = Object.entries(preferences)
      .filter(([key]) => typeof preferences[key] !== 'function')
      .flatMap(([domainKey, prefs]) => 
        Array.isArray(prefs) ? prefs.map(p => ({ ...p, domainId: domainKey })) : []
      );
  }
  
  // For Premium users, use allElements data which has all 45 element scores
  // For other tiers, use topPreferences which has limited data
  const domainPreferences = preferencesArray
    .filter(pref => pref.domainId === domainId)
    .map(pref => {
      // Try to find score from allElements first (has all 45 elements)
      const allElementMatch = userResults?.allElements?.find(
        el => el.elementId === pref.id || el.id === pref.id
      );
      // Fallback to topPreferences
      const topPrefMatch = userResults?.topPreferences?.find(up => up.id === pref.id);
      
      // Get score from allElements (has strengthPercentile) or topPreferences
      const rawScore = allElementMatch?.strengthPercentile || 
                       topPrefMatch?.strengthPercentile || 
                       (topPrefMatch?.score > 1 ? topPrefMatch.score : (topPrefMatch?.score || 0) * 100);
      
      // Get global rank from allElements
      const globalRank = allElementMatch?.rank || 
                         userResults?.topPreferences?.findIndex(up => up.id === pref.id) + 1 || 
                         null;
      
      return {
        ...pref,
        personDescriptor: pref.descriptor || pref.personDescriptor || pref.name,
        score: rawScore,
        rank: globalRank
      };
    });
  
  // Sort by score descending for proper display
  const sortedPreferences = [...domainPreferences].sort((a, b) => b.score - a.score);
  
  switch(tier) {
    case USER_TIERS.FREE:
      return topDomains.includes(domainId) 
        ? sortedPreferences.filter(pref => pref.score > 0).slice(0, 3)
        : [];
    case USER_TIERS.ENHANCED:
      return sortedPreferences.filter(pref => pref.score > 0).slice(0, 8);
    case USER_TIERS.PREMIUM:
      // Premium gets all elements with scores, sorted by score
      return sortedPreferences;
    default:
      return [];
  }
};

const getLockedDomainElements = (domainId, userResults, tier, preferences, USER_TIERS) => {
  const unlocked = getUnlockedDomainElements(domainId, userResults, tier, preferences, USER_TIERS);
  const unlockedIds = new Set(unlocked.map(el => el.id));
  
  // Handle preferences as either an object (with domain keys) or an array
  let preferencesArray = [];
  if (Array.isArray(preferences)) {
    preferencesArray = preferences;
  } else if (preferences && typeof preferences === 'object') {
    // Convert object format to array, adding domainId to each preference
    preferencesArray = Object.entries(preferences)
      .filter(([key]) => typeof preferences[key] !== 'function')
      .flatMap(([domainKey, prefs]) => 
        Array.isArray(prefs) ? prefs.map(p => ({ ...p, domainId: domainKey })) : []
      );
  }
  
  return preferencesArray
    .filter(pref => pref.domainId === domainId && !unlockedIds.has(pref.id))
    .map(pref => ({
      ...pref,
      personDescriptor: pref.descriptor || pref.personDescriptor || pref.name
    }))
    .sort((a, b) => a.name.localeCompare(b.name));
};

const getDomainName = (domainId) => {
  const domainNames = {
    insight: 'Insight',
    vision: 'Vision',
    connection: 'Connection',
    impact: 'Impact',
    structure: 'Structure'
  };
  return domainNames[domainId] || domainId;
};

const getNextTier = (currentTier, USER_TIERS) => {
  switch(currentTier) {
    case USER_TIERS.FREE:
      return 'Enhanced';
    case USER_TIERS.ENHANCED:
      return 'Premium';
    default:
      return 'Enhanced';
  }
};

// Exported helper function for calculating accessible element count
const getAccessibleElementCount = (results, tier) => {
  if (!results?.topPreferences) return 0;
  
  switch(tier) {
    case USER_TIERS.FREE:
      // Free tier: top 3 scoring elements within top 2 domains
      return 3;
    case USER_TIERS.ENHANCED:
      // Enhanced tier: top 8 scoring elements across all domains (full constellation)
      return 8;
    case USER_TIERS.PREMIUM:
      return 45; // All elements
    default:
      return 0;
  }
};

export default CosmicDomainsGalaxy;
export { CosmicAccessIndicator, getAccessibleElementCount };
